package com.fantasytenis.web.utils

import kotlinx.browser.localStorage

/**
 * Web-specific user preferences stored in localStorage.
 *
 * For UI preferences that don't need to sync across platforms.
 * Separate from SharedPrefs which handles auth tokens.
 */
object WebPreferences {

    private const val KEY_SKILL_DRAWER_WIDTH = "baseline_skill_drawer_width"
    private const val DEFAULT_DRAWER_WIDTH = 420

    // First-time user tracking keys
    private const val KEY_ONBOARDING_COMPLETED = "baseline_onboarding_completed"
    private const val KEY_FIRST_LINEUP_COMPLETED = "baseline_first_lineup_completed"
    private const val KEY_FIRST_LIVE_VIEWED = "baseline_first_live_viewed"

    /**
     * Get the saved skill drawer width.
     * Returns default if not set or localStorage unavailable.
     */
    fun getSkillDrawerWidth(): Int {
        return try {
            localStorage.getItem(KEY_SKILL_DRAWER_WIDTH)?.toIntOrNull()
                ?: DEFAULT_DRAWER_WIDTH
        } catch (e: Exception) {
            // localStorage may be unavailable (e.g., private browsing mode)
            DEFAULT_DRAWER_WIDTH
        }
    }

    /**
     * Save the skill drawer width preference.
     */
    fun setSkillDrawerWidth(width: Int) {
        try {
            localStorage.setItem(KEY_SKILL_DRAWER_WIDTH, width.toString())
        } catch (e: Exception) {
            // Silently fail if localStorage unavailable
        }
    }

    /**
     * Clear the skill drawer width preference (reset to default).
     */
    fun clearSkillDrawerWidth() {
        try {
            localStorage.removeItem(KEY_SKILL_DRAWER_WIDTH)
        } catch (e: Exception) {
            // Silently fail if localStorage unavailable
        }
    }

    // ==================== First-Time User Tracking ====================

    /**
     * Check if user has completed the initial onboarding flow.
     */
    fun hasCompletedOnboarding(): Boolean {
        return try {
            localStorage.getItem(KEY_ONBOARDING_COMPLETED) == "true"
        } catch (e: Exception) {
            false
        }
    }

    /**
     * Mark the onboarding flow as completed.
     */
    fun setOnboardingCompleted() {
        try {
            localStorage.setItem(KEY_ONBOARDING_COMPLETED, "true")
        } catch (e: Exception) {
            // Silently fail if localStorage unavailable
        }
    }

    /**
     * Check if user has completed their first lineup.
     * Used to show/hide guided tooltips in LineupBuilder.
     */
    fun hasCompletedFirstLineup(): Boolean {
        return try {
            localStorage.getItem(KEY_FIRST_LINEUP_COMPLETED) == "true"
        } catch (e: Exception) {
            false
        }
    }

    /**
     * Mark first lineup as completed.
     * Called after user successfully submits their first lineup.
     */
    fun setFirstLineupCompleted() {
        try {
            localStorage.setItem(KEY_FIRST_LINEUP_COMPLETED, "true")
        } catch (e: Exception) {
            // Silently fail if localStorage unavailable
        }
    }

    /**
     * Check if user has viewed their first live contest.
     * Used to show helpful hints about the live experience.
     */
    fun hasViewedFirstLive(): Boolean {
        return try {
            localStorage.getItem(KEY_FIRST_LIVE_VIEWED) == "true"
        } catch (e: Exception) {
            false
        }
    }

    /**
     * Mark first live contest view as completed.
     */
    fun setFirstLiveViewed() {
        try {
            localStorage.setItem(KEY_FIRST_LIVE_VIEWED, "true")
        } catch (e: Exception) {
            // Silently fail if localStorage unavailable
        }
    }

    /**
     * Reset all onboarding progress (useful for testing).
     */
    fun resetOnboardingProgress() {
        try {
            localStorage.removeItem(KEY_ONBOARDING_COMPLETED)
            localStorage.removeItem(KEY_FIRST_LINEUP_COMPLETED)
            localStorage.removeItem(KEY_FIRST_LIVE_VIEWED)
        } catch (e: Exception) {
            // Silently fail if localStorage unavailable
        }
    }
}
